/**
 * Shareable fall-color links.
 *
 * `/fall-color` is the public link people post; it redirects into the map at
 * `/maps/fall-color` with the overlay on. `/autumn-color` is the same link for
 * places that say autumn. Nothing here is US-specific: the view is plain
 * lat/lng/zoom so links keep working as model coverage widens.
 *
 * Keep paths in sync with `middleware.ts` and `api/_lib/seo-meta-resolver.js`,
 * which serve the social preview card for these same paths.
 */

export const FALL_COLOR_SHARE_PATH = '/fall-color';
export const FALL_COLOR_SHARE_ALIAS_PATHS = ['/autumn-color'] as const;
export const FALL_COLOR_MAP_PATH = '/maps/fall-color';

/** Regional zoom: enough to see a valley's color, inside the tileset's z4–z10. */
export const FALL_COLOR_SHARE_DEFAULT_ZOOM = 7;

const MIN_ZOOM = 2;
const MAX_ZOOM = 16;

export interface FallColorShareView {
  lat: number;
  lng: number;
  zoom: number;
}

export function isFallColorMapPath(pathname: string): boolean {
  return pathname === FALL_COLOR_MAP_PATH || pathname === `${FALL_COLOR_MAP_PATH}/`;
}

/**
 * Read `?lat=&lng=&z=` (or `zoom=`). Returns null unless both coordinates are
 * valid, so a bare link falls back to the viewer's own location.
 */
export function parseFallColorShareView(search: string): FallColorShareView | null {
  const params = new URLSearchParams(search);
  const latStr = params.get('lat');
  const lngStr = params.get('lng');
  if (latStr == null || lngStr == null || latStr === '' || lngStr === '') return null;
  const lat = Number(latStr);
  const lng = Number(lngStr);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

  const zoomStr = params.get('z') ?? params.get('zoom');
  const zoomNum = zoomStr != null && zoomStr !== '' ? Number(zoomStr) : Number.NaN;
  const zoom = Number.isFinite(zoomNum)
    ? Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoomNum))
    : FALL_COLOR_SHARE_DEFAULT_ZOOM;

  return { lat, lng, zoom };
}

/**
 * `/fall-color?lat=&lng=&z=` for a view, or bare `/fall-color` without one.
 * Four decimals (~11 m) is finer than the overlay resolves and keeps links short.
 */
export function buildFallColorSharePath(view?: FallColorShareView | null): string {
  if (!view || !Number.isFinite(view.lat) || !Number.isFinite(view.lng)) {
    return FALL_COLOR_SHARE_PATH;
  }
  // Mapbox can report longitudes past ±180 after panning across the antimeridian.
  const lng = ((((view.lng + 180) % 360) + 360) % 360) - 180;
  const params = new URLSearchParams({
    lat: view.lat.toFixed(4),
    lng: lng.toFixed(4),
  });
  if (Number.isFinite(view.zoom)) {
    params.set('z', String(Math.round(view.zoom * 10) / 10));
  }
  return `${FALL_COLOR_SHARE_PATH}?${params.toString()}`;
}
